import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from './ventureDetail.module.scss';
import './card.scss';
import { Button, Card, ConfirmModal, Loader, Separator, Typography } from 'src/components/common';
import { test, ventureideabanner, ventureprototypebanner, venturescalingbanner, venturetractionbanner } from 'src/assets/images';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { useCopyToClipboard, useGetIdentity, useUrlParamValue } from 'src/hooks';
import { useNavigate } from 'react-router-dom';
import { clearFocusPoint, getComments, getSingleVenture, incrementCommentsPage, singleVentureSuccess, ventureVerseLike, ventureVerseUnLike } from 'src/store/venture/venture.slice';
import CreateVentureComment from '../createComment/CreateComment';
import { TGetCommentsRequest } from 'src/services/apiEndpoint.types.ts';
import ViewComment from '../view-comment/ViewComment';
import { openPopup } from 'src/store/common/common.slice';
import { socketService } from 'src/services/socket.api.service';
import { CoWorkerList, IncubatorList, TList } from 'src/constants/user.constants';

export default function VentureDetail({ isViewerVentureCreater = false }: { isViewerVentureCreater?: boolean }) {
  // hooks
  const navigate = useNavigate()
  const { getIdentities } = useGetIdentity();
  const {
    singleVenture,
    commentsPageNumber,
    commentsPerPage,
    comments,
    showCommentsId,
    commentsLoading,
    MoreCommentsLeft,
    clickFocusPoint
  } = useAppSelector(
    (store: any) => store.venture
  );
  const isAuthorized: boolean = useAppSelector((store) => store.auth.isAuthorized);
  const profile = useAppSelector((store) => store.profile.profile);
  const company = useAppSelector((store) => store.company.myCompany);
  const dispatch = useAppDispatch();
  const { copyToClipboard } = useCopyToClipboard();
  // ref
  const commentRef = useRef(null);

  // state
  const [isLoading, setIsLoading] = useState(true);
  const [logoLoading, setLogoLoading] = useState(true);
  const [page, setPage] = useState<number>(1)
  const [like, setLike] = useState<{ count: number, isUserLiked: boolean }>({ count: 0, isUserLiked: false });
  const [show, setShow] = useState<boolean>(false);
  const [isScroll, setIsScroll] = useState<boolean>(false)

  // constant 
  const { authenticatedId, loggedInUserType } = getIdentities();
  const bannerArr = [{ type: 'Idea', banner: ventureideabanner }, { type: 'Prototype', banner: ventureprototypebanner }, { type: 'Scaling', banner: venturescalingbanner }, { type: 'Early Traction', banner: venturetractionbanner }]
  const banner = singleVenture?.banner && isLoading ? singleVenture?.banner : bannerArr.find((item) => item.type === singleVenture?.ventureStage)?.banner
  const companyId = singleVenture?.linkCompanyPage?.id;
  const incubatorObject: TList = IncubatorList.find((item) => item?.Name === singleVenture?.incubatorName)
  const coWorkingObject: TList = CoWorkerList.find((item) => item?.Name === singleVenture?.coWorkingName)
  const coWorkingName = !singleVenture?.coWorkingName ? '' : singleVenture?.coWorkingName === 'Others' ? singleVenture?.otherCoWorkingName : singleVenture?.coWorkingName
  const incubatorName = !singleVenture?.incubatorName ? '' : singleVenture?.incubatorName === 'Others' ? singleVenture?.otherIncubatorName : singleVenture?.incubatorName
  // handler 
  function handleShare() {
    // const path = singleVenture?.[singleVenture?.userId ? 'userId' : 'companyId'] === authenticatedId ? `${window.location.host}/my-venture?id=${singleVenture?.id}` : `${window.location.host}/view-venture?id=${singleVenture?.id}`
    const path = `${window.location.host}/view-venture?id=${singleVenture?.id}`
    copyToClipboard({ text: path });
    dispatch(openPopup({
      popupMessage: 'Link copied successfully!',
      popupType: 'success'
    }))
  }
  function handleEdit() {
    navigate(`/post-venture?id=${singleVenture?.id}&type=edit`)
  }
  function handleDiscoverRedirection() {
    navigate(`/view-company?id=${companyId}`)
  }
  function handleNavigate() {
    navigate(`/login?prev=${encodeURIComponent('/venture')}`)
  }
  function handleLike() {
    if (!isAuthorized) {
      setShow(true)
      return
    }
    const payload = {
      [loggedInUserType === 'User' ? 'userId' : 'companyId']: authenticatedId,
      entityId: singleVenture?.id,
      entityType: "Venture Verse"
    }
    if (like.isUserLiked) {
      dispatch(ventureVerseUnLike(payload)).then((response) => {
        if (response.status) {
          const data = { ...singleVenture, likedByUser: false }
          data.Likes = data.Likes?.filter((item) => loggedInUserType === 'User' ? item?.userId !== authenticatedId : item?.Company?.id !== authenticatedId)
          dispatch(singleVentureSuccess(data))
        }
      })
    } else {
      dispatch(ventureVerseLike(payload)).then((response) => {
        if (response.status) {
          const data = { ...singleVenture, likedByUser: true };
          const user = loggedInUserType === 'User' ? { User: profile, userId: authenticatedId } : { Company: company, userId: null }
          data.Likes = [...data.Likes, user]
          dispatch(singleVentureSuccess(data));
          if (singleVenture?.User.id !== authenticatedId) {
            const performAction = (userId, userType) => {
              console.log("userId venture", userId);
              const userIds = [userId];
              if (userId !== authenticatedId) {
                socketService.emit("notifications-unread", {
                  userIds,
                  userType: userType
                });
              }
            };
            performAction(singleVenture?.User.id, "User");
          }
        }
      })
    }
  }

  const handleGetComments = useCallback((number = 0) => {
    const payload: TGetCommentsRequest = {
      userId: authenticatedId,
      userType: loggedInUserType,
      pageNumber: number || commentsPageNumber,
      ventureVerseId: singleVenture?.id,
      commentsPerPage,
      prevPage: number | page
    };
    setPage(commentsPageNumber)
    dispatch(getComments(payload));
  }, [singleVenture]);
  const handleFetchMoreComments = () => {
    dispatch(incrementCommentsPage(1));
  };

  useEffect(() => {
    if (singleVenture) {
      setLike({ count: singleVenture?.Likes?.length || 0, isUserLiked: singleVenture?.likedByUser })
    }
  }, [singleVenture])

  useEffect(() => {
    if (singleVenture) {
      handleGetComments()
    }
  }, [singleVenture, handleGetComments, commentsPageNumber]);

  useEffect(() => {
    if (clickFocusPoint === 'comment') {
      setIsScroll(true);
    }
    dispatch(clearFocusPoint())
  }, [clickFocusPoint])
  useEffect(() => {
    if (isScroll && commentRef?.current) {
      setTimeout(() => {
        commentRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }
  }, [isScroll, commentRef]);

  const websiteUrl = singleVenture?.websiteUrl
    ? /^(http|https):\/\//.test(singleVenture?.websiteUrl)
      ? singleVenture?.websiteUrl
      : `https://${singleVenture?.websiteUrl}`
    : '';
  return (
    <>
      <Card className="venture-card">
        <div className={cx.ventureCardBanner}>
          <img src={banner} alt="banner" onError={() => setIsLoading(false)} />
        </div>
        <div className={`${cx.ventureCardOuter} ${singleVenture?.logo ? cx.compact : ''}`}>
          <div className={cx.ventureCardImageOuter}>
            {singleVenture?.logo && logoLoading && <div className={cx.ventureCardImage} >
              <img src={singleVenture?.logo} alt="logo" onError={() => setLogoLoading(false)} />
            </div>}
            <div className={cx.ventureCardiTitle}>
              <h3>{singleVenture?.title}</h3>
              <div className={cx.ventureCardiTitleSub}>
                {singleVenture?.domain && <h5>{singleVenture?.domain === 'Others' ? singleVenture?.otherDomainInput || '' : singleVenture?.domain}</h5>}
                <div className={cx.upvoteButton}>
                  {!isViewerVentureCreater && <>
                    <Button variant={like.isUserLiked ? "ternary" : 'ternary-flat'} style={{ width: '99px' }} size="extraSmall" onClick={handleLike}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7.57248 14.9799C4.95206 14.9799 3.8754 13.1195 5.18956 10.8474L7.31123 7.1899C8.6254 4.91782 10.7708 4.91782 12.085 7.1899L14.2066 10.8474C15.5208 13.1195 14.4441 14.9799 11.8237 14.9799H7.57248Z"
                          stroke={like.isUserLiked ? "#FFF" : '#FF6239'}
                          strokeWidth="1.08"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                      {like.isUserLiked ? 'Upvoted' : 'Upvote'}
                    </Button>
                    {companyId && <Button variant="ternary" size="extraSmall" style={{ width: '109px' }} onClick={handleDiscoverRedirection}>
                      Discover More
                    </Button>}
                  </>}
                  {isViewerVentureCreater && (
                    <Button variant="ternary" size="extraSmall" onClick={handleEdit} style={{ width: '68px' }}>
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6.09418 1.12924H5.08085C2.54751 1.12924 1.53418 2.14258 1.53418 4.67591V7.71591C1.53418 10.2492 2.54751 11.2626 5.08085 11.2626H8.12085C10.6542 11.2626 11.6675 10.2492 11.6675 7.71591V6.70258M8.07525 2.21858C8.41471 3.42951 9.36218 4.37698 10.5782 4.72151M8.64778 1.64604L4.65525 5.63858C4.50325 5.79058 4.35125 6.08951 4.32085 6.30738L4.10298 7.83244C4.02191 8.38471 4.41205 8.76978 4.96431 8.69378L6.48938 8.47591C6.70218 8.44551 7.00111 8.29351 7.15818 8.14151L11.1507 4.14898C11.8398 3.45991 12.164 2.65938 11.1507 1.64604C10.1374 0.632709 9.33685 0.956976 8.64778 1.64604Z" stroke="white" strokeWidth="0.76" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>

                      Edit
                    </Button>
                  )}
                  {singleVenture?.websiteUrl && (
                    <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
                      <svg
                        width="26"
                        height="25"
                        viewBox="0 0 21 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_599_12687)">
                          <path
                            d="M8.83066 11.0033C9.17708 11.4665 9.61906 11.8497 10.1266 12.127C10.6342 12.4043 11.1954 12.5692 11.7723 12.6105C12.3492 12.6518 12.9282 12.5686 13.4701 12.3664C14.012 12.1643 14.504 11.848 14.9129 11.4389L17.3329 9.01893C18.0676 8.25824 18.4742 7.23941 18.465 6.18188C18.4558 5.12435 18.0316 4.11274 17.2838 3.36493C16.536 2.61712 15.5244 2.19294 14.4668 2.18375C13.4093 2.17456 12.3905 2.58109 11.6298 3.3158L10.2423 4.6952M12.0573 9.39C11.7109 8.92687 11.2689 8.54366 10.7614 8.26636C10.2538 7.98906 9.69257 7.82416 9.11569 7.78285C8.53881 7.74153 7.95979 7.82477 7.4179 8.02691C6.87602 8.22905 6.38394 8.54536 5.97506 8.9544L3.55506 11.3744C2.82035 12.1351 2.41381 13.1539 2.423 14.2115C2.43219 15.269 2.85638 16.2806 3.60419 17.0284C4.352 17.7762 5.36361 18.2004 6.42114 18.2096C7.47866 18.2188 8.49749 17.8122 9.25819 17.0775L10.6376 15.6981"
                            stroke="#FF6239"
                            strokeWidth="1.61333"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_599_12687">
                            <rect
                              width="19.36"
                              height="19.36"
                              fill="white"
                              transform="translate(0.763672 0.517578)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </a>
                  )}

                  <span onClick={handleShare} style={{ cursor: 'pointer' }}>
                    <svg
                      width="26"
                      height="26"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.7388 14.3598C11.2279 14.3598 10.7937 14.181 10.4362 13.8235C10.0786 13.4659 9.8998 13.0317 9.8998 12.5209C9.8998 12.4493 9.90491 12.3753 9.91512 12.2986C9.92534 12.222 9.94066 12.1531 9.9611 12.0918L5.6395 9.57849C5.46582 9.73174 5.2717 9.85178 5.05715 9.93862C4.84261 10.0255 4.61784 10.0689 4.38286 10.0689C3.87204 10.0689 3.43783 9.89009 3.08025 9.53251C2.72267 9.17493 2.54388 8.74073 2.54388 8.2299C2.54388 7.71908 2.72267 7.28487 3.08025 6.92729C3.43783 6.56972 3.87204 6.39093 4.38286 6.39093C4.61784 6.39093 4.84261 6.43435 5.05715 6.52119C5.2717 6.60803 5.46582 6.72807 5.6395 6.88132L9.9611 4.36805C9.94066 4.30675 9.92534 4.23779 9.91512 4.16116C9.90491 4.08454 9.8998 4.01047 9.8998 3.93895C9.8998 3.42813 10.0786 2.99392 10.4362 2.63634C10.7937 2.27877 11.2279 2.09998 11.7388 2.09998C12.2496 2.09998 12.6838 2.27877 13.0414 2.63634C13.399 2.99392 13.5778 3.42813 13.5778 3.93895C13.5778 4.44978 13.399 4.88398 13.0414 5.24156C12.6838 5.59914 12.2496 5.77793 11.7388 5.77793C11.5038 5.77793 11.279 5.73451 11.0645 5.64767C10.8499 5.56083 10.6558 5.44079 10.4821 5.28754L6.16054 7.80081C6.18098 7.86211 6.1963 7.93107 6.20652 8.00769C6.21673 8.08432 6.22184 8.15839 6.22184 8.2299C6.22184 8.30142 6.21673 8.37549 6.20652 8.45211C6.1963 8.52874 6.18098 8.5977 6.16054 8.659L10.4821 11.1723C10.6558 11.019 10.8499 10.899 11.0645 10.8121C11.279 10.7253 11.5038 10.6819 11.7388 10.6819C12.2496 10.6819 12.6838 10.8607 13.0414 11.2182C13.399 11.5758 13.5778 12.01 13.5778 12.5209C13.5778 13.0317 13.399 13.4659 13.0414 13.8235C12.6838 14.181 12.2496 14.3598 11.7388 14.3598ZM11.7388 4.55195C11.9125 4.55195 12.058 4.4932 12.1755 4.37571C12.293 4.25822 12.3518 4.11264 12.3518 3.93895C12.3518 3.76527 12.293 3.61969 12.1755 3.5022C12.058 3.38471 11.9125 3.32596 11.7388 3.32596C11.5651 3.32596 11.4195 3.38471 11.302 3.5022C11.1845 3.61969 11.1258 3.76527 11.1258 3.93895C11.1258 4.11264 11.1845 4.25822 11.302 4.37571C11.4195 4.4932 11.5651 4.55195 11.7388 4.55195ZM4.38286 8.8429C4.55654 8.8429 4.70213 8.78415 4.81962 8.66666C4.93711 8.54917 4.99586 8.40358 4.99586 8.2299C4.99586 8.05622 4.93711 7.91064 4.81962 7.79315C4.70213 7.67566 4.55654 7.61691 4.38286 7.61691C4.20918 7.61691 4.0636 7.67566 3.94611 7.79315C3.82862 7.91064 3.76987 8.05622 3.76987 8.2299C3.76987 8.40358 3.82862 8.54917 3.94611 8.66666C4.0636 8.78415 4.20918 8.8429 4.38286 8.8429ZM11.7388 13.1338C11.9125 13.1338 12.058 13.0751 12.1755 12.9576C12.293 12.8401 12.3518 12.6945 12.3518 12.5209C12.3518 12.3472 12.293 12.2016 12.1755 12.0841C12.058 11.9666 11.9125 11.9079 11.7388 11.9079C11.5651 11.9079 11.4195 11.9666 11.302 12.0841C11.1845 12.2016 11.1258 12.3472 11.1258 12.5209C11.1258 12.6945 11.1845 12.8401 11.302 12.9576C11.4195 13.0751 11.5651 13.1338 11.7388 13.1338Z"
                        fill="#FF6239"
                      />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={cx.upvotesRow}>
          <div className={cx.upvotesCol}>
            <h6>Upvotes</h6>
            <span>
              <svg
                width="24"
                height="24"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.57248 14.9799C4.95206 14.9799 3.8754 13.1195 5.18956 10.8474L7.31123 7.1899C8.6254 4.91782 10.7708 4.91782 12.085 7.1899L14.2066 10.8474C15.5208 13.1195 14.4441 14.9799 11.8237 14.9799H7.57248Z"
                  stroke="#FF6239"
                  strokeWidth="1.08"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
              {like?.count || 0}
            </span>
          </div>
          <div className={cx.upvotesCol}>
            <h6>Feedbacks</h6>
            <span >
              <svg className='me-1' width="18" height="18" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.5 7.85419C14.5023 8.7341 14.2967 9.60211 13.9 10.3875C13.4296 11.3287 12.7065 12.1203 11.8116 12.6737C10.9168 13.2271 9.8855 13.5204 8.83333 13.5209C7.95342 13.5231 7.08541 13.3176 6.3 12.9209L2.5 14.1875L3.76667 10.3875C3.36995 9.60211 3.16437 8.7341 3.16667 7.85419C3.16707 6.80202 3.46041 5.77075 4.01381 4.87589C4.56722 3.98102 5.35884 3.2579 6.3 2.78752C7.08541 2.39081 7.95342 2.18522 8.83333 2.18752H9.16667C10.5562 2.26418 11.8687 2.85069 12.8528 3.83476C13.8368 4.81882 14.4233 6.13129 14.5 7.52085V7.85419Z" stroke="#FF6239" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
              </svg>

              {comments?.length}
            </span>
          </div>
          <div className={cx.upvotesCol}>
            <h6>Founder</h6>
            <span>{singleVenture?.User?.name || singleVenture?.Company?.name}</span>
          </div>
        </div>

        <div className={cx.ventureAbout}>
          <h3>About</h3>
          <div dangerouslySetInnerHTML={{ __html: singleVenture?.about }} />
        </div>

        <div className={cx.aboutTeamRow}>
          {singleVenture?.teamSize?.hidden && <div className={cx.aboutTeamCol}>
            <h4>Team</h4>
            <span><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.20557 5.09766C8.20557 5.99512 7.47803 6.72266 6.58057 6.72266C5.6831 6.72266 4.95557 5.99512 4.95557 5.09766C4.95557 4.20019 5.6831 3.47266 6.58057 3.47266C7.47803 3.47266 8.20557 4.20019 8.20557 5.09766Z" fill="black" fillOpacity="0.15" />
              <path d="M3.33057 10.5143C3.33057 9.31771 4.30062 8.34766 5.49723 8.34766H7.6639C8.86052 8.34766 9.83057 9.31771 9.83057 10.5143V11.056H3.33057V10.5143Z" fill="black" fillOpacity="0.15" />
              <path d="M1.70589 11.056H0.622559V10.5143C0.622559 9.50474 1.31306 8.65643 2.24756 8.41591M3.33089 6.6302C2.69975 6.40712 2.24756 5.8052 2.24756 5.09766C2.24756 4.39013 2.69975 3.78821 3.33089 3.56513M11.4559 11.056H12.5392V10.5143C12.5392 9.50474 11.8487 8.65643 10.9142 8.41591M9.83089 3.56513C10.462 3.78821 10.9142 4.39013 10.9142 5.09766C10.9142 5.8052 10.462 6.40712 9.83089 6.6302M5.49756 8.34766H7.66423C8.86084 8.34766 9.83089 9.31771 9.83089 10.5143V11.056H3.33089V10.5143C3.33089 9.31771 4.30094 8.34766 5.49756 8.34766ZM8.20589 5.09766C8.20589 5.99512 7.47835 6.72266 6.58089 6.72266C5.68343 6.72266 4.95589 5.99512 4.95589 5.09766C4.95589 4.20019 5.68343 3.47266 6.58089 3.47266C7.47835 3.47266 8.20589 4.20019 8.20589 5.09766Z" stroke="black" strokeWidth="1.07535" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
              {singleVenture?.teamSize?.number || 0}</span>
          </div>}
          {singleVenture?.noOfCustomers?.hidden && <div className={cx.aboutTeamCol}>
            <h4>Customers</h4>
            <span><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.20557 5.09766C8.20557 5.99512 7.47803 6.72266 6.58057 6.72266C5.6831 6.72266 4.95557 5.99512 4.95557 5.09766C4.95557 4.20019 5.6831 3.47266 6.58057 3.47266C7.47803 3.47266 8.20557 4.20019 8.20557 5.09766Z" fill="black" fillOpacity="0.15" />
              <path d="M3.33057 10.5143C3.33057 9.31771 4.30062 8.34766 5.49723 8.34766H7.6639C8.86052 8.34766 9.83057 9.31771 9.83057 10.5143V11.056H3.33057V10.5143Z" fill="black" fillOpacity="0.15" />
              <path d="M1.70589 11.056H0.622559V10.5143C0.622559 9.50474 1.31306 8.65643 2.24756 8.41591M3.33089 6.6302C2.69975 6.40712 2.24756 5.8052 2.24756 5.09766C2.24756 4.39013 2.69975 3.78821 3.33089 3.56513M11.4559 11.056H12.5392V10.5143C12.5392 9.50474 11.8487 8.65643 10.9142 8.41591M9.83089 3.56513C10.462 3.78821 10.9142 4.39013 10.9142 5.09766C10.9142 5.8052 10.462 6.40712 9.83089 6.6302M5.49756 8.34766H7.66423C8.86084 8.34766 9.83089 9.31771 9.83089 10.5143V11.056H3.33089V10.5143C3.33089 9.31771 4.30094 8.34766 5.49756 8.34766ZM8.20589 5.09766C8.20589 5.99512 7.47835 6.72266 6.58089 6.72266C5.68343 6.72266 4.95589 5.99512 4.95589 5.09766C4.95589 4.20019 5.68343 3.47266 6.58089 3.47266C7.47835 3.47266 8.20589 4.20019 8.20589 5.09766Z" stroke="black" strokeWidth="1.07535" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
              {singleVenture?.noOfCustomers?.number || 0}</span>
          </div>}
          <div className={cx.aboutTeamCol}>
            <h4>Funding Stage</h4>
            <span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.58598 3.51302V3.14635C4.58598 2.3351 4.58598 1.67969 6.05265 1.67969H6.78598C8.25265 1.67969 8.25265 2.3351 8.25265 3.14635V3.51302M7.33598 7.18885C7.33598 7.18427 7.33598 7.18427 7.33598 7.17969V6.72135C7.33598 6.26302 7.33598 6.26302 6.87765 6.26302H5.96098C5.50265 6.26302 5.50265 6.26302 5.50265 6.72135V7.19344M7.33598 7.18885C7.33598 7.68844 7.3314 8.09635 6.41931 8.09635C5.51181 8.09635 5.50265 7.69302 5.50265 7.19344M7.33598 7.18885C8.57348 7.03302 9.78348 6.57469 10.8422 5.80469M5.50265 7.19344C4.31556 7.06052 3.1514 6.63427 2.12015 5.92844M4.58598 10.8464H8.25265C10.0951 10.8464 10.4251 10.1084 10.5214 9.2101L10.8651 5.54344C10.9889 4.4251 10.6681 3.51302 8.71098 3.51302H4.12765C2.17056 3.51302 1.84973 4.4251 1.97348 5.54344L2.31723 9.2101C2.41348 10.1084 2.74348 10.8464 4.58598 10.8464Z" stroke="#292D32" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {singleVenture?.fundingStage}</span>
          </div>
          <div className={cx.aboutTeamCol}>
            <h4>Venture Stage</h4>
            <span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.58598 3.51302V3.14635C4.58598 2.3351 4.58598 1.67969 6.05265 1.67969H6.78598C8.25265 1.67969 8.25265 2.3351 8.25265 3.14635V3.51302M7.33598 7.18885C7.33598 7.18427 7.33598 7.18427 7.33598 7.17969V6.72135C7.33598 6.26302 7.33598 6.26302 6.87765 6.26302H5.96098C5.50265 6.26302 5.50265 6.26302 5.50265 6.72135V7.19344M7.33598 7.18885C7.33598 7.68844 7.3314 8.09635 6.41931 8.09635C5.51181 8.09635 5.50265 7.69302 5.50265 7.19344M7.33598 7.18885C8.57348 7.03302 9.78348 6.57469 10.8422 5.80469M5.50265 7.19344C4.31556 7.06052 3.1514 6.63427 2.12015 5.92844M4.58598 10.8464H8.25265C10.0951 10.8464 10.4251 10.1084 10.5214 9.2101L10.8651 5.54344C10.9889 4.4251 10.6681 3.51302 8.71098 3.51302H4.12765C2.17056 3.51302 1.84973 4.4251 1.97348 5.54344L2.31723 9.2101C2.41348 10.1084 2.74348 10.8464 4.58598 10.8464Z" stroke="#292D32" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {singleVenture?.ventureStage}</span>
          </div>
          {singleVenture?.coWorkingName && coWorkingObject?.Website ? <a href={coWorkingObject?.Website}
            target="_blank"
            rel="noopener noreferrer"
            className={cx.aboutTeamCol}
          >
            <h4>Co-Working</h4>
            <span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_1540_18304)">
                  <path d="M8.08301 12.7513V5.16797M8.08301 12.7513H4.83301C3.30117 12.7513 2.53472 12.7513 2.05913 12.2752C1.58301 11.7996 1.58301 11.0331 1.58301 9.5013V5.16797C1.58301 3.63614 1.58301 2.86968 2.05913 2.39409C2.53472 1.91797 3.30117 1.91797 4.83301 1.91797C6.36484 1.91797 7.1313 1.91797 7.60688 2.39409C8.08301 2.86968 8.08301 3.63614 8.08301 5.16797M8.08301 12.7513H10.2497C11.2713 12.7513 11.7815 12.7513 12.0989 12.4339C12.4163 12.1165 12.4163 11.6062 12.4163 10.5846V7.33464C12.4163 6.31305 12.4163 5.8028 12.0989 5.48539C11.7815 5.16797 11.2713 5.16797 10.2497 5.16797H8.08301M4.02051 6.79297H3.47884M6.18717 6.79297H5.64551M4.02051 4.6263H3.47884M4.02051 8.95963H3.47884M6.18717 4.6263H5.64551M6.18717 8.95963H5.64551M10.5205 8.95963H9.97884M10.5205 6.79297H9.97884" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <defs>
                  <clipPath id="clip0_1540_18304">
                    <rect width="13" height="13" fill="white" transform="translate(0.5 0.835938)" />
                  </clipPath>
                </defs>
              </svg>

              {coWorkingName?.slice(0, 16) + `${coWorkingName?.length > 16 ? '...' : ''}`}
            </span>
          </a>
            : singleVenture?.coWorkingName ? (
              <div
                className={cx.aboutTeamCol}
              >
                <h4>Co-Working</h4>
                <span>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_1540_18304)">
                      <path d="M8.08301 12.7513V5.16797M8.08301 12.7513H4.83301C3.30117 12.7513 2.53472 12.7513 2.05913 12.2752C1.58301 11.7996 1.58301 11.0331 1.58301 9.5013V5.16797C1.58301 3.63614 1.58301 2.86968 2.05913 2.39409C2.53472 1.91797 3.30117 1.91797 4.83301 1.91797C6.36484 1.91797 7.1313 1.91797 7.60688 2.39409C8.08301 2.86968 8.08301 3.63614 8.08301 5.16797M8.08301 12.7513H10.2497C11.2713 12.7513 11.7815 12.7513 12.0989 12.4339C12.4163 12.1165 12.4163 11.6062 12.4163 10.5846V7.33464C12.4163 6.31305 12.4163 5.8028 12.0989 5.48539C11.7815 5.16797 11.2713 5.16797 10.2497 5.16797H8.08301M4.02051 6.79297H3.47884M6.18717 6.79297H5.64551M4.02051 4.6263H3.47884M4.02051 8.95963H3.47884M6.18717 4.6263H5.64551M6.18717 8.95963H5.64551M10.5205 8.95963H9.97884M10.5205 6.79297H9.97884" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_1540_18304">
                        <rect width="13" height="13" fill="white" transform="translate(0.5 0.835938)" />
                      </clipPath>
                    </defs>
                  </svg>

                  {coWorkingName?.slice(0, 16) + `${coWorkingName?.length > 16 ? '...' : ''}`}
                </span>
              </div>
            ) : <></>}
          {singleVenture?.incubatorName && incubatorObject?.Website ? (
            <a href={incubatorObject?.Website}
              className={cx.aboutTeamCol}
              target="_blank"
              rel="noopener noreferrer"
            >
              <h4>Incubator</h4>
              <span>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.75 6.08504L6.65 8.18504L5.85 6.98504L4.25 8.58504M8.75 6.08504H7.75M8.75 6.08504V7.08504M5.01 1.75504L2.315 3.85504C1.865 4.20504 1.5 4.95004 1.5 5.51504V9.22004C1.5 10.38 2.445 11.33 3.605 11.33H9.395C10.555 11.33 11.5 10.38 11.5 9.22504V5.58504C11.5 4.98004 11.095 4.20504 10.6 3.86004L7.51 1.69504C6.81 1.20504 5.685 1.23004 5.01 1.75504Z" stroke="#292D32" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                {incubatorName?.slice(0, 16) + `${incubatorName?.length > 16 ? '...' : ''}`}
              </span>
            </a>
          ) : singleVenture?.incubatorName ? (
            <div className={cx.aboutTeamCol}>
              <h4>Incubator</h4>
              <span>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.75 6.08504L6.65 8.18504L5.85 6.98504L4.25 8.58504M8.75 6.08504H7.75M8.75 6.08504V7.08504M5.01 1.75504L2.315 3.85504C1.865 4.20504 1.5 4.95004 1.5 5.51504V9.22004C1.5 10.38 2.445 11.33 3.605 11.33H9.395C10.555 11.33 11.5 10.38 11.5 9.22504V5.58504C11.5 4.98004 11.095 4.20504 10.6 3.86004L7.51 1.69504C6.81 1.20504 5.685 1.23004 5.01 1.75504Z" stroke="#292D32" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                {incubatorName?.slice(0, 16) + `${incubatorName?.length > 16 ? '...' : ''}`}
              </span>
            </div>
          ) : <></>}
        </div>

        {/* comments  */}
        <CreateVentureComment ventureVerseId={singleVenture?.id} viewerId={authenticatedId} viewerType={loggedInUserType} />
        {comments?.length > 0 && (
          <>
            <Separator />
            <div className="view-post__comments mt-3">
              {comments.map((item, index) => {
                return (
                  <ViewComment
                    key={item?.id}
                    comment={item}
                    ventureVerseId={singleVenture.id}
                    handleGetComments={handleGetComments}
                    viewerId={authenticatedId}
                    viewerType={loggedInUserType}
                  />
                );
              })}
            </div>
          </>
        )}
        {MoreCommentsLeft && !commentsLoading && (
          <Typography
            as="p"
            variant="caption"
            fontWeight="semiBold"
            className="view-post__load-more-comments"
            onClick={handleFetchMoreComments}
          >
            Show more comments
          </Typography>
        )}
        {commentsLoading && <Loader />}
        <span role='img' ref={commentRef}></span>
      </Card>
      <ConfirmModal
        title=""
        description={<p style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', margin: '0' }}>
          <span>To enjoy all the features of <span style={{ color: '#fa4616' }}>htraction</span> </span>
          <span><span style={{ color: '#fa4616' }}>Sign in</span> to Continue</span>
        </p>}
        openConfirmModal={show}
        onClose={() => setShow(false)}
        onSubmit={handleNavigate}
        submitText="Sign in"
        type="confirm"
      />
    </>
  );
}
